/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { useStrategySchemaV2 } from '../../../hooks/useStrategySchemaV2';

const PlaceholderList = () => (
  <div className="text-xs text-textSecondary italic">Schema not yet parsed into nodes.</div>
);

const SchemaNodePalette = ({ onAdd }) => {
  const { loading, error, schemaHash, normalized, unified } = useStrategySchemaV2() || {};

  if (error) return <div className="text-xs text-red-500">Failed to load schema.</div>;
  if (loading) return <div className="text-xs text-textSecondary">Loading schema…</div>;

  const { indicators = [], operators = [], actions = [] } = normalized || {};
  const empty = [indicators, operators, actions].every(arr => !arr || arr.length === 0);
  const useUnified = empty && Array.isArray(unified) && unified.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold tracking-wide text-textSecondary">Node Palette</h3>
        {schemaHash && <span className="text-[10px] font-mono text-textSecondary/70" title="Schema hash">{schemaHash.slice(0,8)}</span>}
      </div>
      {empty && !useUnified && <PlaceholderList />}
      {(!empty || useUnified) && (
        <>
          {!useUnified && (
            <>
              <Section title="Indicators" items={indicators} onAdd={onAdd} />
              <Section title="Operators" items={operators} onAdd={onAdd} />
              <Section title="Actions" items={actions} onAdd={onAdd} />
            </>
          )}
          {useUnified && <CategoryGroups items={unified} onAdd={onAdd} />}
          <ExpressionSection onAdd={onAdd} />
        </>
      )}
    </div>
  );
};

const Section = ({ title, items, onAdd }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-textSecondary mb-1">{title}</div>
      <ul className="flex flex-wrap gap-1">
        {items.map(it => (
          <li key={it.name}>
            <button
              type="button"
              onClick={() => onAdd && onAdd(it)}
              className="px-2 py-1 rounded-md bg-muted text-[11px] hover:bg-muted/70"
              title={it.name}
            >
              {it.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SchemaNodePalette;

const ExpressionSection = ({ onAdd }) => {
  const item = {
    name: 'Expression',
    kind: 'expression',
    params: [ { name: 'formula', type: 'string', default: '' } ],
    outputs: ['value'],
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-textSecondary mb-1">Derived</div>
      <button
        type="button"
        onClick={() => onAdd(item)}
        className="px-2 py-1 rounded-md bg-muted text-[11px] hover:bg-muted/70"
        title="Add expression node"
      >Expression</button>
    </div>
  );
};

const CategoryGroups = ({ items, onAdd }) => {
  const groups = items.reduce((acc, it) => {
    const cat = it.category || 'Other';
    acc[cat] = acc[cat] || [];
    acc[cat].push(it);
    return acc;
  }, {});
  const ordered = Object.entries(groups).sort((a,b)=>a[0].localeCompare(b[0]));
  return (
    <div className="space-y-3">
      {ordered.map(([cat, list]) => (
        <div key={cat}>
          <div className="text-[10px] uppercase tracking-wide text-textSecondary mb-1">{cat}</div>
          <ul className="flex flex-wrap gap-1">
            {list.map(it => (
              <li key={it.name}>
                <button
                  type="button"
                  onClick={()=>onAdd && onAdd(it)}
                  className="px-2 py-1 rounded-md bg-muted text-[11px] hover:bg-muted/70"
                  title={it.name}
                >{it.name}</button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};
